import { useEffect, useState } from 'react';
import { Actions, Async, FSpread } from '@client/new/helpers/types';
import toast from 'react-hot-toast';
import { BundlesModel } from '../model/subscriptionBundles.model';
import { usePushNotificationModel } from '../model/pushNotification.model';
import { z } from 'zod';
import { Control, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  getPlatformBundleModalCta,
  getPnDefaultMessage,
} from './editPlatformBundle.vm.mapper';
import { format } from 'date-fns';
import { useUrlHash } from '@client/router/useUrlHash';
import { SubscriptionBundle } from '../../common/bundle.types';
import {
  BundlePricing,
  BundleStatus,
  useEnableCheckoutFreeTrialMutation,
} from 'generated/graphql';
import { trackEvent } from '@services/mixpanel';
import { browserHistory } from '@client/router/compat';
import { PATHS } from '@client/routes';

type PayloadMap = {
  editBundle: SubscriptionBundle;
  cancelEditBundle: null;
  discardChanges: null;
  saveAndClose: null;
  openPnModal: null;
  closePnModal: null;
  savePN: null;
  copyUpgradeLink: string;
  createDiscount: null;
  editLP: null;
};

type EditPlatformBundleAction = Actions<PayloadMap>;
export type EditPlatformBundleDispatcher = FSpread<PayloadMap>;

export type EditPlatformBundleVM = {
  editPlatformBundle: (b: SubscriptionBundle) => void;
  platformBundleModal: {
    isOpen: boolean;
    close: () => void;
    message: string;
    sentOn?: string;
    cta: Array<{
      onClick: () => void;
      label: string;
      disabled?: boolean;
    }>;
    count: number;
    price: {
      monthly?: string;
      annual?: string;
    };
    freeTrial: {
      enabled: boolean;
      onChange: (value: boolean) => void;
    };
    save: () => void;
    submitting: boolean;
    hasExclusiveSub: boolean;
    promotions: {
      createDiscount: () => void;
    };
    landingPage: {
      openLinksModal: () => void;
      edit: () => void;
    };
  };
  unsavedChangesModal: {
    isOpen: boolean;
    saveChanges: () => void;
    discard: () => void;
    cancel: () => void;
  };
  pushNotificationModal: {
    title: string;
    isOpen: boolean;
    close: () => void;
    save: () => void;
    submitting: boolean;
    control: Control<PnFormValues, any, PnFormValues>;
  };
};

export const pushNotificationSchema = z.object({
  message: z.string().trim().min(1, { message: 'Message text is required' }),
});

const formDefaultValues = {
  message: undefined,
};

export type PnFormValues = z.infer<typeof pushNotificationSchema>;

const warnBeforeUnload = (ev: BeforeUnloadEvent) => {
  ev.preventDefault();
  ev.returnValue = '';
};

const formatPrice = (price?: number) =>
  price != null ? `$${price.toFixed(2)}` : undefined;

export const useEditPlatformBundleVM = ({
  model,
  openLinksModal,
}: {
  model: Async<BundlesModel>;
  openLinksModal: () => void;
}): EditPlatformBundleVM => {
  const [isSubmitting, setSubmitting] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [bundleId, setBundleId] = useState<number | undefined>(undefined);
  const [pnModalOpen, setPnModalOpen] = useState<boolean>(false);
  const [freeTrialEnabled, setFreeTrialEnabled] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const { sendNotification } = usePushNotificationModel();
  const [enableCheckoutFreeTrialMutation] =
    useEnableCheckoutFreeTrialMutation();

  const { handleSubmit, control, reset } = useForm<PnFormValues>({
    defaultValues: formDefaultValues,
    resolver: zodResolver(pushNotificationSchema),
  });

  useEffect(() => {
    if (!bundleId) {
      reset({ message: undefined }, { keepDefaultValues: false });
      return;
    }

    const bundle =
      model.type === 'fetched'
        ? model.value.initialSubscriptionBundles.find(
            (b) =>
              b.id === bundleId && b.pricing === BundlePricing.Subscription,
          )
        : undefined;

    const creatorName =
      model.type === 'fetched' ? model.value.creatorName : undefined;
    const message =
      bundle?.pushNotificationMessage || getPnDefaultMessage(creatorName);

    reset({ message }, { keepDefaultValues: false });
  }, [bundleId]);

  useEffect(() => {
    if (bundleId && model.type === 'fetched') {
      setFreeTrialEnabled(model.value.enableCheckoutFreeTrial);
    }
  }, [bundleId]);

  const savedFreeTrial =
    model.type === 'fetched' ? model.value.enableCheckoutFreeTrial : false;
  const isDirty = bundleId != null && freeTrialEnabled !== savedFreeTrial;

  const [bundleHash, setBundleHash] = useUrlHash();
  useEffect(() => {
    if (model.type !== 'fetched') return;
    const linked = model.value.initialSubscriptionBundles.find(
      (b) =>
        `${b.id}` === bundleHash &&
        b.pricing === BundlePricing.Subscription &&
        b.isPlatformBundle,
    );
    if (linked?.id === bundleId) return;
    if (isDirty) {
      setBundleHash(`${bundleId}`, { replace: true });
      setShowUnsavedModal(true);
      return;
    }
    setBundleId(linked?.id);
  }, [bundleHash, model.type]);

  useEffect(() => {
    isDirty
      ? window.addEventListener('beforeunload', warnBeforeUnload)
      : window.removeEventListener('beforeunload', warnBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', warnBeforeUnload);
    };
  }, [isDirty]);

  const modelValue = model.type === 'fetched' ? model.value : null;

  const editBundle = (bundle: SubscriptionBundle) => {
    setBundleId(bundle.id);
    setBundleHash(`${bundle.id}`);
  };

  const editedBundle = modelValue?.initialSubscriptionBundles
    .filter((b) => b.isPlatformBundle)
    .find((b) => b.id === bundleId);

  const hasExclusiveSub =
    modelValue?.initialSubscriptionBundles.some(
      (b) => !b.isPlatformBundle && b.status === BundleStatus.Published,
    ) ?? false;

  const dispatch: EditPlatformBundleDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as EditPlatformBundleAction;
    switch (action.type) {
      case 'editBundle':
        editBundle(action.payload);
        return null;
      case 'cancelEditBundle':
        if (isDirty) {
          setShowUnsavedModal(true);
          return null;
        }
        setBundleId(undefined);
        setBundleHash('');
        return null;
      case 'discardChanges':
        setShowUnsavedModal(false);
        setBundleId(undefined);
        setBundleHash('');
        return null;
      case 'saveAndClose':
        saveFreeTrial();
        setShowUnsavedModal(false);
        setBundleId(undefined);
        setBundleHash('');
        return null;
      case 'openPnModal':
        trackEvent({
          type: 'Exclusive Sub Push Created',
          payload: {
            count: (editedBundle?.pushNotificationUsageCount || 0) + 1,
          },
        });
        setPnModalOpen(true);
        return null;
      case 'closePnModal':
        setPnModalOpen(false);
        return null;
      case 'savePN':
        setSubmitting(true);
        sendPN();
        return null;
      case 'copyUpgradeLink':
        trackEvent({ type: 'Exclusive Sub Upgrade Link Copied' });
        copyUpgradeLink(action.payload);
        return null;
      case 'createDiscount':
        browserHistory.push(
          PATHS.PROMOTIONS(modelValue!.creatorId, { mode: 'create' }),
        );
        return null;
      case 'editLP':
        browserHistory.push(PATHS.LANDING_PAGE(modelValue!.creatorId));
        return null;
    }
  };

  const copyUpgradeLink = (link: string) => {
    navigator.clipboard.writeText(link);
    toast.success('Link was copied to clipboard');
  };

  const saveFreeTrial = () => {
    if (!editedBundle?.influencerId) return;
    setSaving(true);
    enableCheckoutFreeTrialMutation({
      variables: {
        data: {
          influencerId: editedBundle.influencerId,
          isEnabled: freeTrialEnabled,
        },
      },
    })
      .then(() => {
        toast.success('Settings saved successfully');
      })
      .catch(() => {
        toast.error('Something went wrong!');
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const sendPN = handleSubmit(
    (values) => {
      if (!editedBundle?.id) {
        toast.error('Could not send push notification. Please refresh page.');
        return;
      }

      sendNotification({
        bundleId: editedBundle.id,
        message: values.message,
      })
        .then(() => {
          trackEvent({
            type: 'Exclusive Sub Push Sent',
            payload: { count: editedBundle.pushNotificationUsageCount + 1 },
          });
          toast.success('Push notification has been sent successfully');
          modelValue?.refetch();
          setPnModalOpen(false);
        })
        .catch((error) => {
          toast.error(error);
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
    () => {
      setSubmitting(false);
    },
  );

  return {
    editPlatformBundle: (b) => dispatch('editBundle', b),
    platformBundleModal: {
      isOpen: !!bundleId,
      close: () => dispatch('cancelEditBundle'),
      message: editedBundle?.pushNotificationMessage || '',
      sentOn: editedBundle?.pushNotificationSentAt
        ? format(
            new Date(editedBundle.pushNotificationSentAt),
            'dd.MM.yyyy, h:mm a',
          )
        : undefined,
      cta: getPlatformBundleModalCta({ editedBundle, dispatch }),
      count: editedBundle?.pushNotificationUsageCount || 0,
      price: {
        monthly: formatPrice(editedBundle?.price.monthly),
        annual: formatPrice(editedBundle?.price.annual),
      },
      freeTrial: {
        enabled: freeTrialEnabled,
        onChange: setFreeTrialEnabled,
      },
      save: saveFreeTrial,
      submitting: isSaving,
      hasExclusiveSub,
      promotions: {
        createDiscount: () => dispatch('createDiscount'),
      },
      landingPage: {
        openLinksModal,
        edit: () => dispatch('editLP'),
      },
    },
    unsavedChangesModal: {
      isOpen: showUnsavedModal,
      saveChanges: () => dispatch('saveAndClose'),
      discard: () => dispatch('discardChanges'),
      cancel: () => setShowUnsavedModal(false),
    },
    pushNotificationModal: {
      title:
        editedBundle?.pushNotificationUsageCount &&
        editedBundle?.pushNotificationUsageCount > 0
          ? 'Edit Push Notification'
          : 'Create Push Notification',
      isOpen: pnModalOpen,
      close: () => dispatch('closePnModal'),
      save: () => dispatch('savePN'),
      submitting: isSubmitting,
      control,
    },
  };
};
